import { useParams, Link } from 'react-router-dom';
import { manhuaDetailMap } from '@/mocks/manhuaData';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useEffect } from 'react';

export default function ChapterPage() {
  const { id, chapterNumber } = useParams<{ id: string; chapterNumber: string }>();

  const manhua = id ? manhuaDetailMap[id] : undefined;
  const chapterNum = chapterNumber ? parseInt(chapterNumber, 10) : 0;
  const chapter = manhua?.chapters.find((c) => c.number === chapterNum);

  useEffect(() => {
    if (manhua && chapterNum) {
      document.title = `${manhua.title} 第${chapterNum}话 - 虚空韩漫网_韩漫在线阅读`;
    }
  }, [manhua, chapterNum]);

  if (!manhua || !chapter) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
          <h1 className="text-2xl font-heading text-foreground-900 mb-2">章节未找到</h1>
          <p className="text-foreground-500 mb-6">该章节不存在或已被移除</p>
          {manhua && (
            <Link to={`/manhua/${id}`} className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              返回作品详情
            </Link>
          )}
        </div>
      </div>
    );
  }

  const prevChapter = chapterNum > 1 ? chapterNum - 1 : null;
  const nextChapter = chapterNum < manhua.totalChapters ? chapterNum + 1 : null;

  return (
    <div className="min-h-screen pt-16">
      <div className="px-4 md:px-6 py-8">
        <div className="max-w-3xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: manhua.title, path: `/manhua/${id}` },
              { label: `第${chapterNum}话 ${chapter.title}` },
            ]}
          />

          <article className="mt-6">
            <div className="bg-background-100 rounded-2xl p-6 md:p-8 text-center mb-8">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 mx-auto mb-4">
                <i className="ri-book-read-line text-2xl text-primary-600"></i>
              </div>
              <h1 className="font-heading text-xl md:text-2xl text-foreground-950 mb-2">
                {manhua.title} · 第{chapterNum}话
              </h1>
              <p className="text-sm text-foreground-600 mb-6">
                {chapter.title}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                {prevChapter ? (
                  <Link
                    to={`/manhua/${id}/chapter/${prevChapter}`}
                    className="px-6 py-3 rounded-lg bg-background-200 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-300 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line mr-1.5"></i>
                    上一话
                  </Link>
                ) : (
                  <span className="px-6 py-3 rounded-lg bg-background-100 text-foreground-400 text-sm font-medium whitespace-nowrap cursor-not-allowed">
                    已经是第一话了
                  </span>
                )}

                <Link
                  to={`/manhua/${id}`}
                  className="px-6 py-3 rounded-lg border border-background-200 text-foreground-600 text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-list-check mr-1.5"></i>
                  返回目录
                </Link>

                {nextChapter ? (
                  <Link
                    to={`/manhua/${id}/chapter/${nextChapter}`}
                    className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    下一话
                    <i className="ri-arrow-right-line ml-1.5"></i>
                  </Link>
                ) : (
                  <span className="px-6 py-3 rounded-lg bg-background-100 text-foreground-400 text-sm font-medium whitespace-nowrap cursor-not-allowed">
                    已是最新话
                  </span>
                )}
              </div>
            </div>

            <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading text-lg text-foreground-950">{manhua.title}</h2>
                  <p className="text-foreground-500 text-xs mt-0.5">{manhua.author}</p>
                </div>
                <div className="text-right">
                  <p className="text-foreground-600 text-sm font-medium">第{chapterNum}话</p>
                  <p className="text-foreground-400 text-xs">{chapter.title}</p>
                </div>
              </div>

              <div className="border-t border-background-200/70 pt-8">
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mb-4">
                    <i className="ri-image-line text-3xl text-foreground-300"></i>
                  </div>
                  <h3 className="text-foreground-600 text-sm font-medium mb-2">
                    漫画图片资源加载中
                  </h3>
                  <p className="text-foreground-400 text-xs max-w-sm leading-relaxed">
                    本章内容正在积极准备中。虚空韩漫网每日更新海量韩漫章节，敬请期待完整内容上线。
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
              {prevChapter ? (
                <Link
                  to={`/manhua/${id}/chapter/${prevChapter}`}
                  className="px-6 py-3 rounded-lg bg-background-200 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-300 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line mr-1.5"></i>
                  上一话
                </Link>
              ) : (
                <span className="px-6 py-3 rounded-lg bg-background-100 text-foreground-400 text-sm font-medium whitespace-nowrap cursor-not-allowed">
                  已经是第一话了
                </span>
              )}

              <Link
                to={`/manhua/${id}`}
                className="px-6 py-3 rounded-lg border border-background-200 text-foreground-600 text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-list-check mr-1.5"></i>
                返回目录
              </Link>

              {nextChapter ? (
                <Link
                  to={`/manhua/${id}/chapter/${nextChapter}`}
                  className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  下一话
                  <i className="ri-arrow-right-line ml-1.5"></i>
                </Link>
              ) : (
                <span className="px-6 py-3 rounded-lg bg-background-100 text-foreground-400 text-sm font-medium whitespace-nowrap cursor-not-allowed">
                  已是最新话
                </span>
              )}
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}